import { hydrateAppData, isJsonLike, tryParseDocumentJson } from '@/components/apps/appDocument';
import { api } from './client';
import { tabService } from './tabs';
import { resolveWorkspaceSessionId } from './workspaceSession';
import type {
  GeneratedApp,
  GeneratedAppComponent,
  GeneratedAppLayout,
  GeneratedAppMetadata,
  GeneratedAppStyles,
  AppGenerationRequest,
  AppType,
  AppStatus,
} from '@/types';

const FRONTEND_APP_TYPES: AppType[] = [
  'productivity',
  'social',
  'e-commerce',
  'blog',
  'portfolio',
  'dashboard',
  'custom',
];

function coerceAppType(raw: unknown): AppType {
  return typeof raw === 'string' ? (raw as AppType) : ('custom' as AppType);
}

function coerceAppStatus(raw: unknown): AppStatus {
  const s = typeof raw === 'string' ? raw : 'completed';
  if (s === 'pending' || s === 'processing' || s === 'completed' || s === 'failed') return s;
  return 'completed';
}

function coerceComponents(raw: unknown): GeneratedAppComponent[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .filter((c) => c && typeof c === 'object')
    .map((c) => c as GeneratedAppComponent);
}

function coerceLayouts(raw: unknown): GeneratedAppLayout[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .filter((l) => l && typeof l === 'object')
    .map((l) => l as GeneratedAppLayout);
}

function coerceStyles(raw: unknown): GeneratedAppStyles | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  return raw as GeneratedAppStyles;
}

function coerceMetadata(raw: unknown): GeneratedAppMetadata | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  return raw as GeneratedAppMetadata;
}

/** Maps Go `models.GeneratedApp` JSON (and legacy UI shapes) to frontend `GeneratedApp`. */
function mapApiAppToGeneratedApp(raw: unknown): GeneratedApp {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const id = String(o.id ?? '');

  const appDataRaw =
    o.app_data != null && typeof o.app_data === 'object' && !Array.isArray(o.app_data)
      ? (o.app_data as Record<string, unknown>)
      : null;
  const appData = hydrateAppData(appDataRaw);
  const titleFromData =
    appData && typeof appData.title === 'string' ? (appData.title as string) : undefined;

  const legacyName = typeof o.name === 'string' ? o.name : undefined;
  const apiAppName = typeof o.app_name === 'string' ? o.app_name : undefined;
  const name = (legacyName ?? apiAppName ?? titleFromData ?? 'Generated app').trim() || 'Generated app';

  const legacyDesc = typeof o.description === 'string' ? o.description : undefined;
  const apiDesc = typeof o.app_description === 'string' ? o.app_description : undefined;
  const contentFromData =
    appData && typeof appData.content === 'string' ? (appData.content as string).trim() : '';
  const summaryFromData =
    appData && typeof appData.summary === 'string' ? (appData.summary as string).trim() : '';

  const pickHumanText = (value: string | undefined): string | undefined => {
    if (!value?.trim()) return undefined;
    const t = value.trim();
    if (isJsonLike(t)) {
      const doc = tryParseDocumentJson(t);
      return doc?.summary ?? doc?.description;
    }
    if (/^Content from \d+ tabs$/i.test(t)) return undefined;
    return t;
  };

  const description =
    pickHumanText(legacyDesc) ??
    pickHumanText(apiDesc) ??
    pickHumanText(summaryFromData) ??
    pickHumanText(
      appData && typeof appData.description === 'string' ? (appData.description as string) : undefined,
    ) ??
    pickHumanText(contentFromData);

  let generated_code: string | undefined;
  const hasRenderableComponents =
    Array.isArray(appData?.components) && (appData!.components as unknown[]).length > 0;

  if (typeof o.generated_code === 'string' && o.generated_code.trim() !== '') {
    const raw = o.generated_code.trim();
    const isStructuredJson =
      raw.startsWith('{') &&
      (raw.includes('"components"') || Boolean(tryParseDocumentJson(raw)?.components?.length));
    if (!isStructuredJson) generated_code = o.generated_code;
  } else if (appData && !hasRenderableComponents) {
    const c = appData.content;
    if (typeof c === 'string' && c.trim() !== '') {
      const t = c.trim();
      const isStructured =
        t.startsWith('{') &&
        (t.includes('"components"') || Boolean(tryParseDocumentJson(t)?.components?.length));
      if (!isStructured) generated_code = c;
    }
  }

  const user_id = typeof o.user_id === 'string' ? o.user_id : '';
  const tech_stack = typeof o.tech_stack === 'string' && o.tech_stack.trim() !== '' ? o.tech_stack : '—';
  const created_at =
    typeof o.created_at === 'string' ? o.created_at : new Date().toISOString();
  const updated_at = typeof o.updated_at === 'string' ? o.updated_at : created_at;

  // Rich fields may live either on the top-level payload (new shape) or inside
  // the app_data JSONB column (legacy / consolidated shape).
  const components = coerceComponents(o.components ?? appData?.components);
  const layouts = coerceLayouts(o.layouts ?? appData?.layouts);
  const styles = coerceStyles(o.styles ?? appData?.styles);
  const metadata = coerceMetadata(o.metadata ?? appData?.metadata);
  const configuration =
    o.configuration && typeof o.configuration === 'object' && !Array.isArray(o.configuration)
      ? (o.configuration as Record<string, unknown>)
      : appData && appData.configuration && typeof appData.configuration === 'object'
        ? (appData.configuration as Record<string, unknown>)
        : undefined;

  return {
    id,
    user_id,
    name,
    app_name: apiAppName,
    description,
    app_description: apiDesc,
    source_url: typeof o.source_url === 'string' ? o.source_url : undefined,
    source_title: typeof o.source_title === 'string' ? o.source_title : undefined,
    source_favicon: typeof o.source_favicon === 'string' ? o.source_favicon : undefined,
    generated_code,
    tech_stack,
    framework: typeof o.framework === 'string' ? o.framework : undefined,
    ui_library: typeof o.ui_library === 'string' ? o.ui_library : undefined,
    app_type: coerceAppType(o.app_type),
    raw_app_type: typeof o.app_type === 'string' ? o.app_type : undefined,
    app_data: appData ?? undefined,
    status: coerceAppStatus(o.status),
    rating: typeof o.rating === 'number' ? o.rating : undefined,
    total_ratings: typeof o.total_ratings === 'number' ? o.total_ratings : undefined,
    is_bookmarked: Boolean(o.is_bookmarked),
    is_shared: Boolean(o.is_shared),
    token_used: typeof o.token_used === 'number' ? o.token_used : undefined,
    generation_model: typeof o.generation_model === 'string' ? o.generation_model : undefined,
    code_quality: typeof o.code_quality === 'number' ? o.code_quality : undefined,
    completeness: typeof o.completeness === 'number' ? o.completeness : undefined,
    best_practices: typeof o.best_practices === 'number' ? o.best_practices : undefined,
    version: typeof o.version === 'number' ? o.version : undefined,
    parent_version_id: typeof o.parent_version_id === 'string' ? o.parent_version_id : undefined,
    preview_url: typeof o.preview_url === 'string' ? o.preview_url : undefined,
    preview_status: typeof o.preview_status === 'string' ? o.preview_status : undefined,
    components,
    layouts,
    styles,
    configuration,
    metadata,
    created_at,
    updated_at,
  };
}

function mapAppsList(apps: unknown[]): GeneratedApp[] {
  return apps.map((a) => mapApiAppToGeneratedApp(a));
}

interface CreateAppRequest {
  tab_ids?: string[];
  app_type: string;
  app_name?: string;
  name?: string;
  description?: string;
  source_url?: string;
  tech_stack?: string;
  session_id?: string;
}

interface UpdateAppRequest {
  name?: string;
  description?: string;
}

interface RateAppRequest {
  rating: number;
}

interface RegenerateAppRequest {
  modifications?: string;
}

export interface AppSuggestion {
  type: AppType;
  name: string;
  description: string;
  confidence: number;
  reason: string;
}

class AppService {
  async getApps(sessionId?: string, page = 1, limit = 100): Promise<GeneratedApp[]> {
    const sid = sessionId ?? (await resolveWorkspaceSessionId());
    const [ownedRes, sharedRes] = await Promise.all([
      sid
        ? api.get<{ apps: unknown[]; total: number }>('/apps', { params: { page, limit, session_id: sid } })
        : Promise.resolve({ data: { apps: [] as unknown[], total: 0 } }),
      api.get<{ apps: unknown[]; total: number }>('/apps/shared-with-me').catch(() => ({
        data: { apps: [] as unknown[], total: 0 },
      })),
    ]);

    const sharedIds = new Set(
      (sharedRes.data.apps ?? []).map((raw) => String((raw as Record<string, unknown>).id ?? ''))
    );
    const merged = new Map<string, GeneratedApp>();

    for (const raw of ownedRes.data.apps ?? []) {
      const app = mapApiAppToGeneratedApp(raw);
      if (app.id) merged.set(app.id, app);
    }
    for (const raw of sharedRes.data.apps ?? []) {
      const app = mapApiAppToGeneratedApp(raw);
      if (!app.id) continue;
      merged.set(app.id, { ...app, is_shared: true });
    }

    // Preserve shared flag even if app also appears in owned list
    for (const id of sharedIds) {
      const existing = merged.get(id);
      if (existing) merged.set(id, { ...existing, is_shared: true });
    }

    return Array.from(merged.values());
  }

  async getSessionApps(sessionId: string, page = 1, limit = 20): Promise<GeneratedApp[]> {
    const response = await api.get<{ apps: unknown[]; total: number }>(
      `/sessions/${sessionId}/apps`,
      { params: { page, limit } }
    );
    return mapAppsList(response.data.apps ?? []);
  }

  async createApp(request: AppGenerationRequest): Promise<GeneratedApp> {
    const sid =
      request.session_id ?? (await resolveWorkspaceSessionId());
    const response = await api.post<unknown>('/apps', {
      ...request,
      session_id: sid,
      tab_ids: request.tab_ids,
    }, { timeout: 120000 });
    return mapApiAppToGeneratedApp(response.data);
  }

  async createSessionApp(
    sessionId: string,
    request: CreateAppRequest,
    options?: { onProgress?: (percent: number, message: string) => void },
  ): Promise<GeneratedApp> {
    const payload = {
      tab_ids: request.tab_ids ?? [],
      app_type: request.app_type,
      app_name: request.app_name ?? request.name,
      description: request.description,
    };

    try {
      const streamed = await tabService.generateAppFromTabsStreamAndWait(sessionId, payload, {
        onProgress: options?.onProgress,
      });
      return mapApiAppToGeneratedApp({
        id: streamed.app_id,
        app_name: streamed.app_name,
        app_type: streamed.app_type ?? request.app_type,
      });
    } catch {
      // Fallback when SSE is unavailable (older API image) — allow up to 5 minutes.
      const response = await api.post<unknown>(`/sessions/${sessionId}/tabs/generate`, payload, {
        timeout: 300_000,
      });
      return mapApiAppToGeneratedApp(response.data);
    }
  }

  async getAppCount(sessionId: string): Promise<{ count: number; by_status: Record<string, number> }> {
    const response = await api.get<{ count: number; by_status: Record<string, number> }>(
      `/sessions/${sessionId}/apps/count`
    );
    return response.data;
  }

  async getBookmarkedApps(sessionId: string): Promise<GeneratedApp[]> {
    const response = await api.get<{ apps: unknown[] }>(`/sessions/${sessionId}/apps/bookmarked`);
    return mapAppsList(response.data.apps ?? []);
  }

  async searchApps(sessionId: string, query: string): Promise<GeneratedApp[]> {
    const response = await api.get<{ apps: unknown[]; total: number }>(
      `/sessions/${sessionId}/apps/search`,
      { params: { q: query } }
    );
    return mapAppsList(response.data.apps ?? []);
  }

  async getApp(appId: string): Promise<GeneratedApp> {
    const response = await api.get<unknown>(`/apps/${appId}`);
    return mapApiAppToGeneratedApp(response.data);
  }

  async getAppFiles(appId: string): Promise<Array<{path: string; content: string; size_bytes: number; mime_type: string}>> {
    const response = await api.get<{ files: Array<{path: string; content: string; size_bytes: number; mime_type: string}> }>(`/apps/${appId}/files`);
    return response.data.files || [];
  }

  async updateApp(appId: string, request: UpdateAppRequest): Promise<GeneratedApp> {
    const response = await api.put<unknown>(`/apps/${appId}`, request);
    return mapApiAppToGeneratedApp(response.data);
  }

  async deleteApp(appId: string): Promise<void> {
    await api.delete(`/apps/${appId}`);
  }

  async rateApp(appId: string, rating: number): Promise<GeneratedApp> {
    const response = await api.post<unknown>(`/apps/${appId}/rate`, { rating } as RateAppRequest);
    return mapApiAppToGeneratedApp(response.data);
  }

  async toggleBookmark(appId: string): Promise<GeneratedApp> {
    const response = await api.post<unknown>(`/apps/${appId}/bookmark`);
    return mapApiAppToGeneratedApp(response.data);
  }

  async duplicateApp(appId: string): Promise<GeneratedApp> {
    const response = await api.post<unknown>(`/apps/${appId}/duplicate`);
    return mapApiAppToGeneratedApp(response.data);
  }

	async deployApp(appId: string): Promise<{ cdn_url: string; message: string }> {
		const response = await api.post<{ cdn_url: string; message: string }>(`/apps/${appId}/deploy`, undefined, {
			timeout: 300000,
		});
    return response.data;
  }

  async regenerateApp(appId: string, modifications?: string): Promise<GeneratedApp> {
    const response = await api.post<unknown>(`/apps/${appId}/regenerate`, {
      modifications,
    } as RegenerateAppRequest, { timeout: 120000 });
    return mapApiAppToGeneratedApp(response.data);
  }

  async getAppsByType(type: AppType, sessionId?: string): Promise<GeneratedApp[]> {
    const sid = sessionId ?? (await resolveWorkspaceSessionId());
    const response = await api.get<{ apps: unknown[] }>(`/apps/type/${type}`, {
      params: sid ? { session_id: sid } : undefined,
    });
    return mapAppsList(response.data.apps ?? []);
  }

  async getSuggestedAppTypes(sessionId: string, tabIds: string[]): Promise<AppSuggestion[]> {
    const response = await api.get<{ suggestions: AppSuggestion[] }>(
      `/sessions/${sessionId}/tabs/suggestions`,
      { params: { tab_ids: tabIds.join(',') } }
    );
    return response.data.suggestions;
  }
}

export const appService = new AppService();